import { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import type { FiscalProfile } from "@/services/fiscalService";

interface Props {
  /** The persisted profile — banner reads `derivedMetadata` off it. */
  profile: FiscalProfile | null | undefined;
}

/**
 * Surfaces the result of the auto-derivation that ran when the customer's
 * DGI registral OCR completed. Two states:
 *
 *   - needsReview=false → green confirmation showing how many obligaciones
 *     matched. Encourages the customer that the auto-fill went well so they
 *     can skim and confirm faster.
 *   - needsReview=true  → yellow warning + collapsible reasons list. Forces
 *     the customer's eyes onto the specific fields the deriver couldn't pin
 *     down.
 *
 * Renders nothing when derivedMetadata is null (e.g., a tenant that signed
 * up manually with no OCR step run).
 */
export function FiscalProfileAutoFillBanner({ profile }: Props): JSX.Element | null {
  const [showDetails, setShowDetails] = useState(false);
  const meta = profile?.derivedMetadata;
  if (!meta) return null;

  const obligacionesCount = meta.trace.obligacionesMatched?.length ?? 0;

  if (meta.needsReview) {
    return (
      <div className="rounded-md border border-amber-500/40 bg-amber-50 p-3 text-amber-900">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="flex-1 space-y-1 text-sm">
            <p className="font-medium">
              Pre-cargamos tu perfil con los datos de DGI — verificá los campos
              señalados
            </p>
            <button
              type="button"
              onClick={() => setShowDetails((s) => !s)}
              className="flex items-center gap-1 text-xs underline-offset-2 hover:underline"
            >
              {showDetails ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              {meta.reviewReasons.length} observación
              {meta.reviewReasons.length === 1 ? "" : "es"} a revisar
            </button>
            {showDetails ? (
              <ul className="mt-1 list-disc pl-5 space-y-0.5 text-xs">
                {meta.reviewReasons.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-emerald-500/40 bg-emerald-50 p-3 text-emerald-900">
      <div className="flex items-start gap-2">
        <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />
        <div className="flex-1 text-sm">
          <p className="font-medium">
            Pre-cargamos tu perfil con los datos registrales de DGI
          </p>
          <p className="text-xs mt-0.5">
            Detectamos {obligacionesCount} obligación
            {obligacionesCount === 1 ? "" : "es"} fiscal
            {obligacionesCount === 1 ? "" : "es"}. Revisá y confirmá los datos
            antes de continuar.
          </p>
        </div>
      </div>
    </div>
  );
}
